import { toast, type ExternalToast } from 'sonner';

export type NotifyOptions = Omit<
  ExternalToast,
  'icon' | 'description' | 'action' | 'duration' | 'id'
> & {
  description?: string;
  action?: { label: string; onClick: () => void };
  duration?: number;
  id?: string | number;
};

type PromiseMessages<T> = {
  loading: string;
  success: string | ((value: T) => string);
  error: string | ((err: unknown) => string);
};

function build(opts?: NotifyOptions): ExternalToast {
  if (!opts) return {};
  const { action, ...rest } = opts;
  return action ? { ...rest, action: { label: action.label, onClick: action.onClick } } : rest;
}

interface AxiosLikeError {
  message?: string;
  response?: {
    status?: number;
    statusText?: string;
    data?: { message?: string; description?: string; error?: string } | string;
  };
  request?: unknown;
}

function asAxiosError(err: unknown): AxiosLikeError | null {
  return err && typeof err === 'object' ? (err as AxiosLikeError) : null;
}

function statusTitle(status: number): string {
  if (status === 401 || status === 403) return 'Not authorized';
  if (status === 404) return 'Not found';
  if (status === 409) return 'Conflict';
  if (status === 422) return 'Validation failed';
  if (status === 429) return 'Too many requests';
  if (status >= 500) return 'Server error';
  if (status >= 400) return 'Request failed';
  return 'Request failed';
}

function statusFallback(status: number): string {
  if (status === 401 || status === 403) return 'You do not have permission to perform this action.';
  if (status === 404) return 'The requested resource was not found.';
  if (status === 409) return 'This action conflicts with the current state. Refresh and try again.';
  if (status === 422) return 'The submitted data failed validation.';
  if (status === 429) return 'Too many requests. Wait a moment and try again.';
  if (status === 500) return 'The server hit an unexpected error. Try again in a moment.';
  if (status === 502 || status === 503 || status === 504)
    return 'The service is temporarily unavailable. Try again shortly.';
  if (status >= 500) return 'The server failed to respond. Try again in a moment.';
  if (status >= 400) return 'The request could not be completed.';
  return '';
}

function extractServerMessage(data: unknown): string | undefined {
  if (!data) return undefined;
  if (typeof data === 'string') return data;
  if (typeof data === 'object') {
    const d = data as { message?: string; description?: string; error?: string };
    return d.description || d.message || d.error || undefined;
  }
  return undefined;
}

/**
 * Extracts a title + description pair from an unknown error.
 * Title is short and actionable. Description carries the technical detail.
 */
export function describeError(
  err: unknown,
  fallbackTitle = 'Something went wrong'
): {
  title: string;
  description?: string;
} {
  const axiosErr = asAxiosError(err);

  if (axiosErr?.response) {
    const status = axiosErr.response.status ?? 0;
    const serverMsg = extractServerMessage(axiosErr.response.data);
    return {
      title: statusTitle(status),
      description: serverMsg || statusFallback(status) || axiosErr.message,
    };
  }

  if (axiosErr?.request) {
    return {
      title: 'Network error',
      description: 'Could not reach the server. Check your connection and retry.',
    };
  }

  if (err instanceof Error) {
    return { title: fallbackTitle, description: err.message };
  }

  if (typeof err === 'string') {
    return { title: fallbackTitle, description: err };
  }

  return { title: fallbackTitle };
}

export const notify = {
  success: (message: string, opts?: NotifyOptions) => toast.success(message, build(opts)),
  error: (message: string, opts?: NotifyOptions) =>
    toast.error(message, build({ duration: opts?.action ? Infinity : 6000, ...opts })),
  warning: (message: string, opts?: NotifyOptions) => toast.warning(message, build(opts)),
  info: (message: string, opts?: NotifyOptions) => toast.info(message, build(opts)),
  loading: (message: string, opts?: NotifyOptions) => toast.loading(message, build(opts)),
  message: (message: string, opts?: NotifyOptions) => toast(message, build(opts)),
  promise: <T>(p: Promise<T>, msgs: PromiseMessages<T>) => toast.promise(p, msgs),
  dismiss: (id?: string | number) => toast.dismiss(id),
  fromError: (err: unknown, opts?: NotifyOptions & { fallbackTitle?: string }) => {
    const { fallbackTitle, ...rest } = opts ?? {};
    const { title, description } = describeError(err, fallbackTitle);
    return toast.error(title, build({ description, ...rest }));
  },
};

const batchTimers = new Map<string, ReturnType<typeof setTimeout>>();
const batchCounts = new Map<string, number>();

export function notifyBatched(
  key: string,
  build: (count: number) => { message: string; opts?: NotifyOptions },
  windowMs = 2000
) {
  const current = (batchCounts.get(key) ?? 0) + 1;
  batchCounts.set(key, current);

  const existing = batchTimers.get(key);
  if (existing) clearTimeout(existing);

  const t = setTimeout(() => {
    const count = batchCounts.get(key) ?? current;
    const { message, opts } = build(count);
    notify.error(message, { id: key, ...opts });
    batchTimers.delete(key);
    batchCounts.delete(key);
  }, windowMs);

  batchTimers.set(key, t);
}
